import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import './ConsoleDashboardShell.css'

const miniLogo = new URL(
  '../../vendor/metronic-v9.4.13/assets/media/app/mini-logo-gray.svg',
  import.meta.url,
).href
const miniLogoDark = new URL(
  '../../vendor/metronic-v9.4.13/assets/media/app/mini-logo-gray-dark.svg',
  import.meta.url,
).href
const avatar = new URL(
  '../../vendor/metronic-v9.4.13/assets/media/avatars/300-2.png',
  import.meta.url,
).href

export interface ConsoleSystemNavItem {
  id: string
  label: string
  href: string
  glyph: string
  iconClass?: string
  active?: boolean
  meta?: string
}

export interface ConsoleSidebarItem {
  label: string
  href: string
  active?: boolean
  meta?: string
  iconClass?: string
  badgeTone?: 'success' | 'destructive' | 'info'
}

export interface ConsoleSidebarSection {
  title: string
  items: ConsoleSidebarItem[]
}

export interface ConsoleToolbarTab {
  label: string
  href: string
  active?: boolean
  meta?: string
  iconClass?: string
}

interface ConsoleDashboardShellProps {
  brandLabel?: string
  backgroundImage?: string
  moduleLabel: string
  moduleSubtitle?: string
  eyebrow?: string
  systems: ConsoleSystemNavItem[]
  sidebarSections: ConsoleSidebarSection[]
  toolbarTabs?: ConsoleToolbarTab[]
  toolbarMeta?: ReactNode
  children: ReactNode
  'data-testid'?: string
}

const layoutStyle = {
  '--sidebar-width': '300px',
  '--sidebar-collapsed-width': '60px',
  '--sidebar-header-height': '54px',
  '--header-height': '60px',
  '--toolbar-height': '54px',
  '--header-height-mobile': '60px',
} as CSSProperties

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

function icon(item: { iconClass?: string; glyph?: string }) {
  if (item.iconClass) return <i className={`ki-filled ${item.iconClass}`} />
  return <span className="text-xs font-semibold">{item.glyph}</span>
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function ConsoleDashboardShell({
  brandLabel = 'Pheme AI',
  backgroundImage,
  moduleLabel,
  moduleSubtitle,
  eyebrow = 'Home',
  systems,
  sidebarSections,
  toolbarTabs = [],
  toolbarMeta,
  children,
  'data-testid': testId = 'console-dashboard-shell',
}: ConsoleDashboardShellProps) {
  const [systemSwitcherOpen, setSystemSwitcherOpen] = useState(false)
  const [systemMenuPosition, setSystemMenuPosition] = useState({ left: 112, top: 68 })
  const systemSwitcherRef = useRef<HTMLDivElement | null>(null)
  const systemMenuRef = useRef<HTMLDivElement | null>(null)
  const activeSystem = systems.find((item) => item.active) ?? systems[0]

  const updateSystemMenuPosition = () => {
    const trigger = systemSwitcherRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const menuWidth = 260
    setSystemMenuPosition({
      left: clamp(rect.left - 6, 12, window.innerWidth - menuWidth - 12),
      top: rect.bottom + 8,
    })
  }

  useEffect(() => {
    const root = document.documentElement
    const previousTheme = root.getAttribute('data-kt-theme')
    const previousMode = root.getAttribute('data-kt-theme-mode')
    const hadLight = root.classList.contains('light')

    root.setAttribute('data-kt-theme', 'true')
    root.setAttribute('data-kt-theme-mode', 'light')
    root.classList.add('light')

    return () => {
      if (previousTheme === null) root.removeAttribute('data-kt-theme')
      else root.setAttribute('data-kt-theme', previousTheme)

      if (previousMode === null) root.removeAttribute('data-kt-theme-mode')
      else root.setAttribute('data-kt-theme-mode', previousMode)

      if (!hadLight) root.classList.remove('light')
    }
  }, [])

  useEffect(() => {
    if (!systemSwitcherOpen) return
    updateSystemMenuPosition()

    function closeOnOutsidePointer(event: PointerEvent) {
      const target = event.target as Node
      if (
        !systemSwitcherRef.current?.contains(target) &&
        !systemMenuRef.current?.contains(target)
      ) {
        setSystemSwitcherOpen(false)
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setSystemSwitcherOpen(false)
    }

    document.addEventListener('pointerdown', closeOnOutsidePointer)
    document.addEventListener('keydown', closeOnEscape)
    window.addEventListener('resize', updateSystemMenuPosition)
    window.addEventListener('scroll', updateSystemMenuPosition, true)

    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer)
      document.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('resize', updateSystemMenuPosition)
      window.removeEventListener('scroll', updateSystemMenuPosition, true)
    }
  }, [systemSwitcherOpen])

  const shellStyle = {
    ...layoutStyle,
    ...(backgroundImage
      ? { '--console-dashboard-background': `url("${backgroundImage}")` }
      : {}),
  } as CSSProperties

  return (
    <div
      aria-label={moduleSubtitle ? `${moduleLabel}: ${moduleSubtitle}` : moduleLabel}
      className={cx(
        'metronic-layout-14 flex grow',
        backgroundImage && 'metronic-layout-14--with-background',
      )}
      data-sidebar-open="true"
      data-testid={testId}
      style={shellStyle}
    >
      <header className="flex items-stretch fixed z-10 top-0 start-0 end-0 shrink-0 bg-background/95 border-b border-border backdrop-blur-sm supports-backdrop-filter:bg-background/60 h-(--header-height-mobile) lg:h-(--header-height) pe-[var(--removed-body-scroll-bar-size,0px)]">
        <div className="@container grow pe-5 flex items-stretch justify-between gap-2.5">
          <div className="flex items-stretch gap-x-6">
            <div className="flex border-e border-border items-center gap-2 lg:w-(--sidebar-width)">
              <div className="flex items-center w-full">
                <div className="flex items-center justify-center shrink-0 border-e border-border w-(--sidebar-collapsed-width) h-(--header-height) bg-muted">
                  <a href={activeSystem?.href ?? '/pheme'}>
                    <img alt={`${activeSystem?.label ?? brandLabel} logo`} className="dark:hidden min-h-[30px]" src={miniLogo} />
                    <img alt={`${activeSystem?.label ?? brandLabel} logo`} className="hidden dark:block min-h-[30px]" src={miniLogoDark} />
                  </a>
                </div>

                <div className="flex w-full grow items-center justify-between px-5 gap-2.5">
                  <button className="kt-btn kt-btn-ghost lg:hidden" type="button">
                    <i className="ki-filled ki-menu" />
                  </button>
                  <div className="metronic-layout-14__system-switcher" ref={systemSwitcherRef}>
                    <button
                      aria-controls="console-system-switcher-menu"
                      aria-expanded={systemSwitcherOpen}
                      aria-haspopup="menu"
                      aria-label={`Abrir menu de sistemas (${brandLabel})`}
                      className="metronic-layout-14__system-switcher-trigger cursor-pointer group focus-visible:outline-hidden items-center justify-center has-data-[arrow=true]:justify-between whitespace-nowrap font-medium ring-offset-background transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0 hover:bg-accent data-[state=open]:bg-accent data-[state=open]:text-accent-foreground h-8.5 rounded-md gap-1.5 text-[0.8125rem] leading-(--text-sm--line-height) [&_svg:not([class*=size-])]:size-4 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg:not([role=img]):not([class*=text-]):not([class*=opacity-])]:opacity-60 select-none inline-flex text-muted-foreground hover:text-foreground px-1.5 -ms-1.5"
                      data-state={systemSwitcherOpen ? 'open' : 'closed'}
                      onClick={() => {
                        updateSystemMenuPosition()
                        setSystemSwitcherOpen((open) => !open)
                      }}
                      type="button"
                    >
                      <div className="metronic-layout-14__system-switcher-current-icon size-6 flex items-center justify-center rounded-md bg-teal-600 text-white">
                        {activeSystem ? icon(activeSystem) : <i className="ki-filled ki-flash-circle" />}
                      </div>
                      <span className="text-mono text-sm font-medium hidden lg:block">
                        {brandLabel}
                      </span>
                      <i className="ki-filled ki-arrow-up-down" />
                    </button>

                    {systemSwitcherOpen && createPortal(
                      <div
                        aria-label="Sistemas"
                        className="metronic-layout-14__system-menu"
                        id="console-system-switcher-menu"
                        ref={systemMenuRef}
                        role="menu"
                        style={{
                          '--console-system-menu-left': `${systemMenuPosition.left}px`,
                          '--console-system-menu-top': `${systemMenuPosition.top}px`,
                        } as CSSProperties}
                      >
                        {systems.map((item) => (
                          <a
                            aria-current={item.active ? 'page' : undefined}
                            className={cx(
                              'metronic-layout-14__system-menu-item',
                              item.active && 'is-active',
                            )}
                            href={item.href}
                            key={item.id}
                            onClick={() => setSystemSwitcherOpen(false)}
                            role="menuitem"
                          >
                            <span className="metronic-layout-14__system-menu-icon">
                              {icon(item)}
                            </span>
                            <span className="metronic-layout-14__system-menu-copy">
                              <strong>{item.label}</strong>
                              {item.meta && <span>{item.meta}</span>}
                            </span>
                            {item.active && <i className="ki-filled ki-check" />}
                          </a>
                        ))}
                      </div>,
                      document.body,
                    )}
                  </div>
                  <button
                    className="cursor-pointer group focus-visible:outline-hidden items-center justify-center has-data-[arrow=true]:justify-between whitespace-nowrap font-medium ring-offset-background transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-60 [&_svg]:shrink-0 hover:bg-accent data-[state=open]:bg-accent data-[state=open]:text-accent-foreground rounded-md gap-1.5 text-[0.8125rem] leading-(--text-sm--line-height) focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shrink-0 w-8.5 h-8.5 p-0 [&_svg:not([class*=size-])]:size-4 hidden lg:inline-flex text-muted-foreground hover:text-foreground"
                    data-slot="button"
                    type="button"
                  >
                    <i className="ki-filled ki-grid" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-row items-center flex-wrap gap-1 lg:gap-5 mb-5 lg:mb-0 hidden lg:flex">
              <ol className="kt-breadcrumb">
                <li className="kt-breadcrumb-item">
                  <a className="kt-breadcrumb-link" href="/cockpit">
                    {eyebrow}
                  </a>
                </li>
                <li className="kt-breadcrumb-separator">/</li>
                <li className="kt-breadcrumb-item">
                  <span className="kt-breadcrumb-page">{moduleLabel}</span>
                </li>
              </ol>
            </div>
          </div>

          <nav className="flex items-center gap-2.5">
            <button className="kt-btn kt-btn-outline px-2" type="button">
              <i className="ki-filled ki-message-text" />
            </button>
            <button className="kt-btn kt-btn-outline px-2" type="button">
              <i className="ki-filled ki-user-square" />
            </button>
            <button className="kt-btn kt-btn-outline px-2" type="button">
              <i className="ki-filled ki-add-files" />
            </button>
            <div className="kt-input hidden lg:flex">
              <i className="ki-filled ki-magnifier" />
              <input className="kt-input" placeholder="Search" type="text" />
            </div>
            <button className="kt-btn kt-btn-outline px-2" type="button">
              <i className="ki-filled ki-clipboard" />
              <span className="hidden lg:block">Reports</span>
            </button>
            <button className="kt-btn kt-btn-mono" type="button">
              <i className="ki-filled ki-plus" />
              <span className="hidden lg:block">Add</span>
            </button>
            <div className="cursor-pointer shrink-0">
              <div className="kt-avatar size-7">
                <div className="kt-avatar-image">
                  <img alt="avatar" src={avatar} />
                </div>
                <div className="kt-avatar-indicator -end-1.5 -top-1.5">
                  <div className="kt-avatar-status kt-avatar-status-online size-2.5" />
                </div>
              </div>
            </div>
          </nav>
        </div>
      </header>

      <aside
        className="fixed overflow-hidden top-(--header-height) start-0 z-20 bottom-0 transition-all duration-300 flex items-stretch flex-shrink-0 w-(--sidebar-width) in-data-[sidebar-open=false]:w-(--sidebar-collapsed-width) border-e border-border [--kt-drawer-enable:true] lg:[--kt-drawer-enable:false]"
        id="sidebar"
      >
        <div className="flex flex-col items-center justify-center shrink-0 py-2.5 gap-5 w-(--sidebar-collapsed-width) border-e border-input bg-muted">
          <div className="relative overflow-hidden grow w-full h-[calc(100vh-5.5rem)]">
            <div className="h-full w-full rounded-[inherit]">
              <div className="grow gap-1 shrink-0 flex items-center flex-col">
                {systems.map((item) => (
                  <a
                    key={item.id}
                    className={cx(
                      'kt-btn kt-btn-icon',
                      item.active ? 'kt-btn-mono active' : 'kt-btn-ghost',
                    )}
                    href={item.href}
                    title={item.label}
                  >
                    {icon(item)}
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2.5 shrink-0">
            <a className="kt-btn kt-btn-icon kt-btn-ghost" href="#">
              <i className="ki-filled ki-messages" />
            </a>
            <a className="kt-btn kt-btn-icon kt-btn-ghost" href="#">
              <i className="ki-filled ki-notepad" />
            </a>
            <a className="kt-btn kt-btn-icon kt-btn-ghost" href="#">
              <i className="ki-filled ki-setting-2" />
            </a>
          </div>
        </div>

        <div className="metronic-layout-14__sidebar-secondary grow shrink-0 h-[calc(100vh-1rem)] lg:h-[calc(100vh-4rem)] mt-0 mb-2.5 overflow-y-auto">
          <div className="flex px-5 pt-2.5 pb-4 shrink-0">
            <div className="kt-input">
              <input
                aria-label={`Buscar em ${moduleLabel}`}
                className="kt-input"
                placeholder="Search"
                type="text"
              />
              <span className="kt-badge kt-badge-stroke">⌘ K</span>
            </div>
          </div>

          <div className="kt-accordion" data-kt-accordion="true">
            {sidebarSections.map((section, sectionIndex) => (
              <div className="kt-accordion-item active" data-kt-accordion-item="true" key={section.title}>
                {sectionIndex > 0 && <div className="shrink-0 bg-border h-px w-full my-2.5" />}
                {sectionIndex > 0 && (
                  <button className="kt-accordion-toggle px-3" data-kt-accordion-toggle="true" type="button">
                    <span className="kt-accordion-title">
                      <div className="px-2 text-xs font-normal text-muted-foreground">
                        {section.title}
                      </div>
                    </span>
                    <span aria-hidden="true" className="kt-accordion-indicator">
                      <i className="ki-filled ki-plus kt-accordion-indicator-on" />
                      <i className="ki-filled ki-minus kt-accordion-indicator-off" />
                    </span>
                  </button>
                )}

                <div className="kt-accordion-content">
                  <div className="kt-accordion-wrapper">
                    <ul
                      className="kt-accordion-menu gap-px"
                      data-kt-accordion="true"
                      data-kt-accordion-initialized="true"
                    >
                      {section.items.map((item) => (
                        <li
                          className={cx(
                            'kt-accordion-menu-item w-full space-y-7.5 px-2.5',
                            item.active && 'selected',
                          )}
                          data-kt-accordion-item="true"
                          key={`${section.title}-${item.href}-${item.label}`}
                        >
                          <a
                            className="kt-accordion-menu-link kt-accordion-selected:text-muted-foreground text-sm font-normal h-8.5 px-2.5"
                            href={item.href}
                          >
                            {item.iconClass && (
                              <i className={`ki-filled ${item.iconClass} text-base kt-accordion-selected:text-muted-foreground`} />
                            )}
                            {item.label}
                            {item.meta && (
                              <span
                                className={cx(
                                  'kt-badge kt-badge-xs kt-badge-light ms-auto',
                                  item.badgeTone === 'destructive' && 'kt-badge-destructive',
                                  item.badgeTone === 'success' && 'kt-badge-success',
                                  item.badgeTone === 'info' && 'kt-badge-info',
                                )}
                              >
                                {item.meta}
                              </span>
                            )}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>

      <div className="metronic-layout-14__workspace grow overflow-y-auto pt-(--header-height-mobile) lg:pt-[calc(var(--header-height)+var(--toolbar-height))] lg:ps-(--sidebar-width) lg:in-data-[sidebar-open=false]:ps-(--sidebar-collapsed-width) transition-all duration-300">
        <div className="metronic-layout-14__toolbar py-2.5 lg:py-0 lg:fixed top-(--header-height) start-[calc(var(--sidebar-width))] lg:in-data-[sidebar-open=false]:start-[calc(var(--sidebar-collapsed-width))] transition-all duration-300 end-0 z-10 px-5 flex flex-wrap items-center justify-between gap-2.5 min-h-(--toolbar-height) bg-background border-b border-border shrink-0">
          <div className="flex flex-col md:flex-row md:items-center flex-wrap gap-1 lg:gap-5">
            <div className="flex items-stretch">
              <nav className="list-none flex items-stretch gap-2">
                {toolbarTabs.map((tab) => (
                  <a
                    className={cx(
                      'kt-btn kt-btn-sm kt-btn-ghost inline-flex items-center text-sm font-medium',
                      tab.active
                        ? 'bg-muted text-foreground'
                        : 'text-secondary-foreground hover:text-primary',
                    )}
                    href={tab.href}
                    key={`${tab.href}-${tab.label}`}
                  >
                    {tab.iconClass && <i className={`ki-filled ${tab.iconClass}`} />}
                    {tab.label}
                    {tab.meta && (
                      <span className="kt-badge kt-badge-xs kt-badge-light ms-1">
                        {tab.meta}
                      </span>
                    )}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            {toolbarMeta && <span className="text-sm text-muted-foreground me-1">{toolbarMeta}</span>}
            <button className="kt-btn kt-btn-sm kt-btn-outline" type="button">
              <i className="ki-filled ki-filter" />
              Sort
            </button>
            <button className="kt-btn kt-btn-sm kt-btn-outline" type="button">
              <i className="ki-filled ki-eye" />
              View
            </button>
            <button className="kt-btn kt-btn-sm kt-btn-outline" type="button">
              <i className="ki-filled ki-setting-4" />
              Filter
            </button>
            <button className="kt-btn kt-btn-sm kt-btn-outline kt-btn-icon" type="button">
              <i className="ki-filled ki-magnifier" />
            </button>
          </div>
        </div>

        <main className="grow p-5 metronic-layout-14__content" role="content">
          {children}
        </main>
      </div>
    </div>
  )
}
